/**
 * Rendered <title> rules (SEO crawl fixes, Oct 2026).
 *
 * Shared by api/ssr.ts and src/ (src re-exports this module), so the server
 * override and the Helmet title follow one rule.
 */

export const BRAND_TITLE_SUFFIX = ' | Journal Party';
export const MAX_BRANDED_TITLE_LENGTH = 60;

/**
 * Append " | Journal Party" only when the result stays within 60 characters;
 * otherwise return the title alone. A title that already carries the suffix
 * is normalized first, so a hard-coded long "... | Journal Party" loses it too.
 */
export function formatPageTitle(title: string): string {
  const base = stripBrandSuffix(title.trim());
  const branded = `${base}${BRAND_TITLE_SUFFIX}`;
  return branded.length <= MAX_BRANDED_TITLE_LENGTH ? branded : base;
}

/**
 * For titles rendered as-is (titleAbsolute): keep them, but drop a trailing
 * " | Journal Party" when it pushes the title past 60 characters.
 */
export function enforceBrandSuffixLimit(title: string): string {
  const trimmed = title.trim();
  if (trimmed.length <= MAX_BRANDED_TITLE_LENGTH) return trimmed;
  return stripBrandSuffix(trimmed);
}

function stripBrandSuffix(title: string): string {
  return title.endsWith(BRAND_TITLE_SUFFIX)
    ? title.slice(0, -BRAND_TITLE_SUFFIX.length).trimEnd()
    : title;
}

const PODCAST_SUFFIX = ': Journaling Podcast';
const PODCAST_LOWERCASE_WORDS = new Set(['To', 'For', 'On']);
// Cutting a long after-colon segment happens before its first connector word,
// so the title ends on a full phrase ("...: 5 Journal Prompts").
const PODCAST_CONNECTOR_WORDS = new Set(['for', 'to', 'on', 'with', 'and', 'of', 'in', 'about']);

function cutAtWordBoundary(text: string, max: number): string {
  if (text.length <= max) return text;
  const slice = text.slice(0, max + 1);
  const lastSpace = slice.lastIndexOf(' ');
  return (lastSpace > 0 ? slice.slice(0, lastSpace) : text.slice(0, max)).replace(/[\s,:;&-]+$/, '');
}

/**
 * Podcast episode titles: " - " becomes ": ", Title Case "To/For/On" become
 * lowercase (not as a segment's first word), ": Journaling Podcast" is
 * appended only when the total stays within 60 characters, and a topic over
 * 60 characters is cut after its colon segment.
 */
export function formatPodcastEpisodeTitle(topicTitle: string): string {
  const normalized = topicTitle
    .trim()
    .replace(/\s+[-–—]\s+/g, ": ")
    .replace(/\s+/g, ' ')
    .split(': ')
    .map((segment) =>
      segment
        .split(' ')
        .map((word, index) => (index > 0 && PODCAST_LOWERCASE_WORDS.has(word) ? word.toLowerCase() : word))
        .join(' '),
    )
    .join(': ');

  if (`${normalized}${PODCAST_SUFFIX}`.length <= MAX_BRANDED_TITLE_LENGTH) {
    return `${normalized}${PODCAST_SUFFIX}`;
  }
  if (normalized.length <= MAX_BRANDED_TITLE_LENGTH) return normalized;

  const colonIndex = normalized.indexOf(': ');
  if (colonIndex > 0 && colonIndex + 2 < MAX_BRANDED_TITLE_LENGTH) {
    const head = normalized.slice(0, colonIndex + 2);
    const words = normalized.slice(colonIndex + 2).split(' ');
    const connectorIndex = words.findIndex((word, index) => index > 0 && PODCAST_CONNECTOR_WORDS.has(word.toLowerCase()));
    if (connectorIndex > 0) {
      const phrase = `${head}${words.slice(0, connectorIndex).join(' ')}`;
      if (phrase.length <= MAX_BRANDED_TITLE_LENGTH) return phrase;
    }
    return cutAtWordBoundary(normalized, MAX_BRANDED_TITLE_LENGTH);
  }
  return cutAtWordBoundary(normalized, MAX_BRANDED_TITLE_LENGTH);
}

/**
 * Exact rendered titles from the crawl-fixes spec (Oct 14, 2026), keyed by
 * path. These render as-is (no suffix rule) so the spec's final strings win,
 * and one table is the single source of truth for SEO.tsx, the SSR override
 * and App.tsx's route tracking titles.
 *
 * Not here on purpose: /journal-prompts/for-teens (its title ships with the
 * teen rewrite, PR #773), and /faq/best-app-for-journaling and
 * /faq/what-can-i-journal-about-every-day (the suffix rule alone produces the
 * spec title). Podcast titles come from formatPodcastEpisodeTitle.
 */
export const EXACT_PAGE_TITLES: Readonly<Record<string, string>> = {
  '/journaling-techniques': '12 Journaling Techniques (With Examples and Prompts)',
  '/journal-prompts-for-mental-health': 'Journal Prompts for Mental Health and Hard Days',
  '/journal-prompts/august': 'August Journal Prompts: 31 Daily Prompts',
  '/journal-prompts/september': 'September Journal Prompts: 30 Daily Prompts',
  '/journal-prompts/october': 'October Journal Prompts: 31 Daily Prompts',
  '/journaling-club': 'Journaling Club: Join Live Sessions or Start Your Own',
  '/journal-prompts/for-therapy-clients': 'Journal Prompts for Between Therapy Sessions',
  // SEO-PILLAR-1028: keep the count equal to TOTAL_PROMPT_COUNT (crawlFixTitles.test.ts checks it).
  '/journal-prompts': '185 Journal Prompts by Theme (Journaling Prompts for 2026)',
  '/journal-prompts/fun': '84 fun journal prompts for adults, funny and whimsical',
  // ready-to-ship.md §A (ctr-rewrites final table), Oct 2026.
  '/faq/3-3-3-journal-method': 'The 3-3-3 Journal Method: How It Works (With Examples)',
  '/faq/3-2-1-journaling-method': 'The 3-2-1 Journaling Method, Explained (With Template)',
  '/podcast': 'Journaling Podcast: Guided Prompts You Can Write Along To',
  '/about': 'About Journal Party: Guided Journaling for Paper Journalers',
  '/live': 'Live Journaling Sessions on YouTube | Journal Party',
  '/faq/completely-free-journal-apps': 'Are There Completely Free Journal Apps? (2026)',
  '/guided-journaling-app': 'Guided Journaling App: Prompts, Timer and Music',
  '/ai-journaling-alternative': 'AI Journaling Alternative: Guided Prompts, Real Paper',
  '/best-guided-journaling-platform': 'Choosing a Guided Journaling Platform (Updated Oct 2026)',
  '/faq/5-minute-journal-prompts': '5-Minute Journal Prompts for Morning, Evening and Stress',
  '/journal-prompts/astrology': 'Astrology Journal Prompts: Reflect by Sign and Season',
  '/best-journaling-app-with-prompts': 'Journal Prompts Apps Compared (Updated Oct 2026)',
  '/journal-prompts/planning': 'Planning Journal Prompts: Map Your Week, Month and Year',
  '/journal-prompts/quick': 'Quick Journal Prompts: 5-Minute Journaling That Counts',
  '/journal-prompts/productivity': 'Productivity Journal Prompts: Focus on What Matters',
  '/journal-sessions': 'Journal Sessions: Prompts, Timers and Handwritten Reflection',
  '/journal-prompts/positivity': 'Positivity Journal Prompts: Grounded Optimism',
  '/journal-prompts/communication': 'Communication Journal Prompts for Better Conversations',
  '/journal-prompts/everyday-rituals': 'Everyday Rituals Journal Prompts for Daily Reflection',
  '/journal-prompts/holidays': 'Holiday Journal Prompts: Stay Grounded Through the Season',
  '/journal-prompts/decisions': 'Decision Making Journal Prompts: Get Unstuck With Clarity',
  '/journal-prompts/for-couples': 'Journal Prompts for Couples: Relationship Reflection',
  '/journal-prompts/for-women': 'Journal Prompts for Women: 50+ for Every Season of Life',
  '/journal-prompts/depression': 'Depression Journal Prompts for Hard Days',
  '/journal-prompts/goal-setting': 'Goal Setting Journal Prompts: Plan Goals You Reach',
  '/journal-prompts/mindfulness': 'Mindfulness Journal Prompts: Come Back to the Present',
  '/journal-prompts/routine': 'Routine Journal Prompts for Steadier Days | Journal Party',
  '/journaling-ideas': "Journaling Ideas: What to Write About When You're Stuck",
  '/journal-prompts/advanced': 'Advanced Journal Prompts for Experienced Journalers',
  // Over 65 in the Oct 14 crawl but missing from the spec table (proposed;
  // Steve to confirm on the re-crawl).
  '/journal-prompts/for-therapists': 'Journal Prompts for Therapists: Between-Session Reflection',
  '/journal-prompts/for-coaches': 'Journaling Prompts for Coaches: Structured Reflection',
  '/journal-prompts/for-beginners': 'Journal Prompts for Beginners: How to Start Writing',
  '/journal-prompts/for-students': 'Journal Prompts for Students: A Guide for Educators',
  '/journal-prompts/self-love': 'Self-Love Journal Prompts for Self-Respect and Kindness',
  '/journal-prompts/healing': 'Healing Journal Prompts: 32 Questions for an Old Hurt',
  '/journal-prompts/overthinking': 'Journaling for Overthinking: 32 Prompts to Untangle a Worry',
  '/journaling-challenge': '30-Day Journaling Challenge (2026): Full Prompt Calendar',
};

export function normalizeTitlePath(path: string): string {
  const withoutOrigin = path.replace(/^https?:\/\/[^/]+/i, '');
  const pathname = withoutOrigin.split(/[?#]/)[0] || '/';
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

export function getExactPageTitle(path: string | null | undefined): string | null {
  if (!path) return null;
  return EXACT_PAGE_TITLES[normalizeTitlePath(path)] ?? null;
}
